import React from 'react';
import { Globe, Landmark, Heart, BookOpen } from 'lucide-react';
import { cultureImages } from '../mockData';
import '../styles/projectsoftpower.css';

const Culture = () => {
  const heritagePrograms = [
    {
      icon: Landmark,
      title: 'Heritage Site Preservation',
      description: 'Working with UNESCO to protect and restore endangered cultural sites worldwide',
      impact: '150+ sites'
    },
    {
      icon: Heart,
      title: 'Endangered Traditions',
      description: 'Documenting and supporting at-risk cultural practices and indigenous knowledge',
      impact: '300+ traditions'
    },
    {
      icon: BookOpen,
      title: 'Oral History Archive',
      description: 'Preserving stories, languages, and wisdom from elders and cultural keepers',
      impact: '5,000+ recordings'
    },
    {
      icon: Globe,
      title: 'Cultural Mapping',
      description: 'Creating comprehensive digital maps of global cultural heritage and practices',
      impact: '127 countries'
    }
  ];

  return (
    <div className="culture-page">
      {/* Hero Section */}
      <section className="section-spacing-large" style={{
        background: 'linear-gradient(135deg, var(--color-gray-50) 0%, var(--color-white) 100%)',
        textAlign: 'center'
      }}>
        <div className="container-psp">
          <h1 className="hero-title">Cultural Heritage</h1>
          <p className="hero-tagline" style={{ maxWidth: '800px', margin: '1.5rem auto 0' }}>
            Preserving humanity's diverse cultural legacy. From ancient traditions to contemporary 
            practices, we protect and celebrate the world's cultural richness.
          </p>
        </div>
      </section>

      {/* Heritage Gallery */}
      <section className="section-spacing-large" style={{ background: 'var(--color-gray-50)' }}>
        <div className="container-psp">
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-2xl)' }}>
            <h2 className="section-title">Global Cultural Heritage</h2>
            <p className="body-text" style={{ marginTop: '1rem' }}>
              Exploring traditions, festivals, and heritage sites from around the world
            </p>
          </div>

          <div className="grid-3">
            {cultureImages.map((culture, index) => (
              <div
                key={index}
                className="card-psp image-overlay"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <img
                  src={culture.url}
                  alt={culture.title}
                  className="card-image"
                  style={{ height: '350px' }}
                />
                <div className="overlay-content">
                  <span style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.75rem',
                    background: 'var(--color-emerald)',
                    color: 'var(--color-white)',
                    borderRadius: '1rem',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    marginBottom: '0.75rem'
                  }}>
                    {culture.region}
                  </span>
                  <h3 className="card-title" style={{ color: 'var(--color-white)', marginBottom: '0.5rem' }}>
                    {culture.title}
                  </h3>
                  <p className="caption-text" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    {culture.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Heritage Programs */}
      <section className="section-spacing-large" style={{ background: 'var(--color-white)' }}>
        <div className="container-psp">
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-2xl)' }}>
            <h2 className="section-title">Our Heritage Programs</h2>
            <p className="body-text" style={{ marginTop: '1rem' }}>
              Comprehensive initiatives to preserve and celebrate cultural heritage
            </p>
          </div>

          <div className="grid-2">
            {heritagePrograms.map((program, index) => {
              const IconComponent = program.icon;
              return (
                <div
                  key={index}
                  className="card-psp fade-in-up"
                  style={{
                    background: 'var(--color-gray-50)',
                    padding: 'var(--spacing-xl)',
                    border: '1px solid var(--color-gray-200)',
                    animationDelay: `${index * 0.15}s`
                  }}
                >
                  <div style={{
                    display: 'inline-flex',
                    padding: '0.75rem',
                    borderRadius: '0.75rem',
                    background: 'var(--color-emerald)',
                    color: 'var(--color-white)',
                    marginBottom: 'var(--spacing-md)'
                  }}>
                    <IconComponent size={28} />
                  </div>
                  <h3 className="card-title" style={{ marginBottom: '0.75rem' }}>
                    {program.title}
                  </h3>
                  <p className="card-description" style={{ marginBottom: '1rem' }}>
                    {program.description}
                  </p>
                  <div style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.75rem',
                    background: 'var(--color-emerald)',
                    color: 'var(--color-white)',
                    borderRadius: '1rem',
                    fontSize: '0.8125rem',
                    fontWeight: '600'
                  }}>
                    {program.impact}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Regional Focus */}
      <section className="section-spacing-large" style={{ background: 'var(--color-gray-50)' }}>
        <div className="container-psp">
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-2xl)' }}>
            <h2 className="section-title">Regional Cultural Initiatives</h2>
            <p className="body-text" style={{ marginTop: '1rem' }}>
              Supporting cultural preservation across all continents
            </p>
          </div>

          <div className="grid-3">
            {[
              {
                region: 'Asia',
                focus: 'Traditional Arts & Festivals',
                description: 'Preserving ancient crafts, temple arts, and cultural festivals across South, East, and Southeast Asia',
                color: 'var(--color-saffron)'
              },
              {
                region: 'Africa',
                focus: 'Oral Traditions & Indigenous Knowledge',
                description: 'Documenting storytelling traditions, tribal customs, and ancestral wisdom across the continent',
                color: 'var(--color-emerald)'
              },
              {
                region: 'Europe',
                focus: 'Heritage Site Restoration',
                description: 'Protecting architectural heritage, medieval traditions, and historic cultural centers',
                color: 'var(--color-indigo)'
              },
              {
                region: 'Americas',
                focus: 'Indigenous Communities',
                description: 'Supporting Native American, Andean, and Amazonian cultural practices and languages',
                color: 'var(--color-royal-blue)'
              },
              {
                region: 'Middle East',
                focus: 'Ancient Heritage Protection',
                description: 'Safeguarding archaeological sites, traditional arts, and centuries-old cultural practices',
                color: 'var(--color-gold)'
              },
              {
                region: 'Pacific',
                focus: 'Island Cultures & Ocean Heritage',
                description: 'Preserving Polynesian, Melanesian, and Micronesian maritime traditions and art forms',
                color: 'var(--color-emerald)'
              }
            ].map((initiative, index) => (
              <div
                key={index}
                className="card-psp"
                style={{
                  background: 'var(--color-white)',
                  padding: 'var(--spacing-lg)',
                  borderTop: `4px solid ${initiative.color}`
                }}
              >
                <div style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  background: initiative.color,
                  color: 'var(--color-white)',
                  borderRadius: '1rem',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  marginBottom: '0.75rem'
                }}>
                  {initiative.region}
                </div>
                <h3 className="card-title" style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
                  {initiative.focus}
                </h3>
                <p className="card-description">{initiative.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Indigenous Rights Section */}
      <section className="section-spacing-large" style={{ background: 'var(--color-white)' }}>
        <div className="container-psp">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="section-title" style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)' }}>
              Indigenous Cultural Rights
            </h2>
            <div className="accent-border" style={{ padding: 'var(--spacing-xl)', background: 'var(--color-gray-50)' }}>
              <p className="body-text" style={{ fontSize: '1.125rem', lineHeight: '1.8', marginBottom: '1.5rem' }}>
                We are committed to the UN Declaration on the Rights of Indigenous Peoples. Our work 
                centers indigenous voices, respects traditional knowledge systems, and supports 
                self-determination in cultural preservation efforts.
              </p>
              <p className="body-text" style={{ fontSize: '1.125rem', lineHeight: '1.8' }}>
                All cultural documentation and preservation projects are conducted with free, prior, 
                and informed consent, ensuring that communities maintain sovereignty over their cultural heritage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-spacing-large" style={{
        background: 'linear-gradient(135deg, var(--color-emerald) 0%, var(--color-indigo) 100%)',
        color: 'var(--color-white)',
        textAlign: 'center'
      }}>
        <div className="container-psp">
          <h2 className="section-title" style={{ color: 'var(--color-white)' }}>
            Support Heritage Preservation
          </h2>
          <p className="body-text" style={{
            color: 'rgba(255, 255, 255, 0.95)',
            maxWidth: '600px',
            margin: '1.5rem auto 2rem'
          }}>
            Join us in protecting endangered cultural traditions and ensuring they thrive for future generations.
          </p>
          <button className="btn-primary" style={{
            background: 'var(--color-white)',
            color: 'var(--color-emerald)',
            border: '2px solid var(--color-white)'
          }}>
            Contribute to Preservation
          </button>
        </div>
      </section>
    </div>
  );
};

export default Culture;
